<template>
  <div class="topbar">
    <div class="topbar-left">
      <button class="mobile-menu-toggle d-md-none" @click="toggleMobileMenu">
        <i class="fas fa-bars"></i>
      </button>
      <h1 class="page-title">{{ currentPageTitle }}</h1>
    </div>
    
    <div class="topbar-center">
      <div class="search-box">
        <i class="fas fa-search search-icon"></i>
        <input 
          type="text" 
          placeholder="Search..." 
          class="search-input"
          v-model="searchQuery"
          @input="handleSearch"
        >
      </div>
    </div>
    
    <div class="topbar-right">
      <button class="topbar-btn" title="Ayuda" @click="showHelp">
        <i class="fas fa-question-circle"></i>
      </button>
      
      <button class="topbar-btn" title="Notificaciones">
        <i class="fas fa-bell"></i>
        <span v-if="notificationCount > 0" class="notification-badge">{{ notificationCount }}</span>
      </button>
      
      <button class="topbar-btn" title="Configuración" @click="showSettings">
        <i class="fas fa-cog"></i>
      </button>
      
      <div class="profile-dropdown" @click="toggleProfileMenu">
        <div class="profile-avatar">
          <img v-if="userAvatar" :src="userAvatar" :alt="userName" class="avatar-img">
          <i v-else class="fas fa-user"></i>
        </div>
        <span class="profile-name d-none d-md-inline">{{ userFirstName }}</span>
        <i class="fas fa-chevron-down dropdown-arrow"></i>
        
        <!-- Dropdown Menu -->
        <div class="profile-menu" v-show="showProfileDropdown">
          <router-link to="/profile" class="profile-menu-item">
            <i class="fas fa-user me-2"></i>
            Mi Perfil
          </router-link>
          <router-link to="/settings" class="profile-menu-item">
            <i class="fas fa-cog me-2"></i>
            Configuración
          </router-link>
          <div class="profile-menu-divider"></div>
          <button @click="logout" class="profile-menu-item logout-btn">
            <i class="fas fa-sign-out-alt me-2"></i>
            Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'TopBar',
  data() {
    return {
      searchQuery: '',
      showProfileDropdown: false
    };
  },
  computed: {
    ...mapGetters(['stateUser']),
    currentPageTitle() {
      const routeMap = {
        '/dashboard': 'Expedientes',
        '/notifications': 'Notificaciones',
        '/favorites': 'Favoritos',
        '/profile': 'Mi Perfil',
        '/settings': 'Configuración'
      };
      
      const currentPath = this.$route.path;
      for (const [path, title] of Object.entries(routeMap)) {
        if (currentPath.startsWith(path)) {
          return title;
        }
      }
      return 'Expedientes';
    },
    userName() {
      return this.stateUser ? `${this.stateUser.first_name} ${this.stateUser.last_name}` : '';
    },
    userFirstName() {
      return this.stateUser ? this.stateUser.first_name : '';
    },
    userAvatar() {
      // Return user avatar URL if available
      return this.stateUser?.avatar || null;
    },
    notificationCount() {
      // This should come from your notifications store
      return 3; // placeholder
    }
  },
  methods: {
    toggleMobileMenu() {
      this.$emit('toggle-sidebar');
    },
    handleSearch() {
      this.$emit('search', this.searchQuery);
    },
    showHelp() {
      // Emit event or show help modal
      this.$emit('show-help');
    },
    showSettings() {
      this.$router.push('/settings');
    },
    toggleProfileMenu() {
      this.showProfileDropdown = !this.showProfileDropdown;
    },
    async logout() {
      await this.$store.dispatch('logOut');
      this.$router.push('/');
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showProfileDropdown = false;
      }
    });
  }
});
</script>

<style scoped>
.topbar {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 280px;
  right: 0;
  height: 70px;
  z-index: 999;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.topbar-left {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.mobile-menu-toggle {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  margin-right: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.mobile-menu-toggle:hover {
  background: #f8f9fa;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.topbar-center {
  flex: 1;
  max-width: 500px;
  margin: 0 2rem;
}

.search-box {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #dee2e6;
  border-radius: 25px;
  background: #f8f9fa;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #4361ee;
  background: white;
  box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.topbar-btn {
  background: none;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  color: #6c757d;
  font-size: 1.1rem;
  transition: all 0.2s;
  position: relative;
}

.topbar-btn:hover {
  background: #f8f9fa;
  color: #495057;
}

.notification-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #ff4757;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  line-height: 1;
}

.profile-dropdown {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid transparent;
}

.profile-dropdown:hover {
  background: #f8f9fa;
  border-color: #dee2e6;
}

.profile-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 0.5rem;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-name {
  font-weight: 500;
  color: #495057;
  margin-right: 0.5rem;
}

.dropdown-arrow {
  font-size: 0.8rem;
  color: #6c757d;
  transition: transform 0.2s;
}

.profile-dropdown:hover .dropdown-arrow {
  transform: rotate(180deg);
}

.profile-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 200px;
  z-index: 1000;
  margin-top: 0.5rem;
}

.profile-menu-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #495057;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  transition: background-color 0.2s;
}

.profile-menu-item:hover {
  background: #f8f9fa;
  color: #495057;
}

.logout-btn:hover {
  background: #fff5f5;
  color: #dc3545;
}

.profile-menu-divider {
  height: 1px;
  background: #dee2e6;
  margin: 0.5rem 0;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .topbar {
    left: 0;
    padding: 1rem;
  }
  
  .topbar-center {
    margin: 0 1rem;
  }
  
  .page-title {
    font-size: 1.25rem;
  }
  
  .profile-name {
    display: none !important;
  }
}
</style>
